<script lang="ts">
  import { Check, AlertTriangle, Undo2 } from "lucide-svelte";
  import type { WalletBenefit, WalletCard } from "../wallet/types";
  import { categoryLabel } from "../wallet/data";
  import { confidenceTier, confidenceLabel, confidenceBadgeVariant } from "./bucketize";
  import { feedbackOf, type BenefitFeedback } from "./feedback";
  import { formatCnyApprox, daysSinceReview } from "./lib/format";
  import Badge from "./ui/Badge.svelte";
  import { cn } from "./lib/utils";

  export let benefit: WalletBenefit;
  export let selectedCards: WalletCard[];
  export let feedbackMap: Record<string, BenefitFeedback>;
  export let muted = false;
  export let onFeedback: (benefitId: string, kind: "used" | "rule_wrong") => void;

  $: tier = confidenceTier(benefit);
  $: cardName = selectedCards.find(c => c.id === benefit.cardId)?.shortName ?? benefit.cardId;
  $: fb = feedbackOf(feedbackMap, benefit.id);
  $: reviewDays = daysSinceReview(benefit.lastReviewedAt);
  $: needsReReview = reviewDays !== null && reviewDays > 90;
</script>

<li
  class={cn(
    "rounded-lg px-3 py-3 transition-colors",
    muted ? "border border-dashed bg-background/30 opacity-80" : "bg-secondary"
  )}
>
  <div class="flex flex-wrap items-start justify-between gap-2">
    <div class="text-sm font-medium">{benefit.title}</div>
    <Badge variant={confidenceBadgeVariant(tier)} class="text-[10px] shrink-0">
      {confidenceLabel(tier)}
    </Badge>
  </div>
  <div class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
    <Badge variant="outline" class="text-[10px]">{categoryLabel(benefit.category)}</Badge>
    <span>·</span>
    <span>{cardName}</span>
    {#if benefit.estimateValueCny > 0}
      <span>·</span>
      <span>{formatCnyApprox(benefit.estimateValueCny)}</span>
    {/if}
    {#if needsReReview}
      <span>·</span>
      <span class="rounded-full bg-warn/10 px-1.5 py-0.5 text-[9px] text-warn" title="超过 90 天未复核">久未复核</span>
    {/if}
  </div>
  <div class="mt-1.5 text-xs">{benefit.usageChannel}</div>

  <div class="mt-2.5 flex items-center gap-2 border-t border-dashed border-foreground/10 pt-2.5">
    {#if fb}
      <span
        class={cn(
          "inline-flex items-center gap-1 text-[11px]",
          fb.kind === "used" ? "text-emerald-500" : "text-amber-500"
        )}
      >
        {#if fb.kind === "used"}
          <Check class="size-3" />
          你标了：用过
        {:else}
          <AlertTriangle class="size-3" />
          你标了：规则不对
        {/if}
      </span>
      <button
        type="button"
        class="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        on:click={() => onFeedback(benefit.id, fb.kind)}
      >
        <Undo2 class="size-3" />
        撤回
      </button>
    {:else}
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/5 hover:text-emerald-500"
        on:click={() => onFeedback(benefit.id, "used")}
      >
        <Check class="size-3" />
        用过
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/5 hover:text-amber-500"
        on:click={() => onFeedback(benefit.id, "rule_wrong")}
      >
        <AlertTriangle class="size-3" />
        规则不对
      </button>
    {/if}
  </div>
</li>
